import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import * as Sentry from "@sentry/react";

import { AuthZProvider } from "@features/authz/AuthZProvider";
import { authZSessionFrom } from "@features/authz/authZSession";
import { useLoadActivePluginsQuery } from "@features/plugins/orgPluginLoader";
import {
  pluginSetMatchesOrganization,
  useActivePluginSet,
} from "@features/plugins/orgPlugins";
import { setProductView } from "@redux/slices/globalStateSlice";

import {
  type AuthRoutingResources,
  AuthRoutingResourcesContext,
  AuthSessionContext,
  AuthUserProviderContext,
  toAuthUserContextValue,
} from "./AuthUserContext";
import { useCurrentUserQuery } from "./data/currentUserQueries";
import useAuthToken from "./useAuthToken";

/**
 * Owns session, current-user, and plugin resources above the router.
 *
 * Plugin routes must be known when the data router is created, so plugin
 * loading cannot live inside the routed application layout. This provider
 * publishes the focused resource results; `AppRouterCoordinator` is the single
 * place that resolves them into a router target.
 */
const AuthUserProvider = ({ children }) => {
  const dispatch = useDispatch();
  const authState = useAuthToken();
  const [appStateInit, setAppStateInit] = useState(false);

  const currentTerritoryId = useSelector(
    (state: any) => state.currentUser.currentTerritoryId
  );
  const currentChannelId = useSelector(
    (state: any) => state.currentUser.currentChannelId
  );

  const { data, error: userError } = useCurrentUserQuery({
    enabled: authState.status === "ready",
  });
  const organization =
    authState.status === "ready" ? data?.organization : undefined;
  const activePluginsQuery = useLoadActivePluginsQuery(organization);
  const activePluginSet = useActivePluginSet(organization);

  useEffect(() => {
    if (userError !== null && userError !== undefined && !data) {
      console.error("Brandhub failed to load [current-user]", userError);
      Sentry.captureException(userError, {
        tags: { bootstrapSource: "current-user" },
      });
    }
  }, [data, userError]);

  useEffect(() => {
    const hasMatchingPluginSet =
      !!organization &&
      !!activePluginSet &&
      pluginSetMatchesOrganization(activePluginSet, organization);
    if (activePluginsQuery.error && !hasMatchingPluginSet) {
      console.error(
        "Brandhub failed to load [plugins]",
        activePluginsQuery.error
      );
      Sentry.captureException(activePluginsQuery.error, {
        tags: { bootstrapSource: "plugins" },
      });
    }
  }, [activePluginSet, activePluginsQuery.error, organization]);

  useEffect(() => {
    if (appStateInit || !data) return;

    Sentry.setUser({ id: data.id, email: data.email });
    Sentry.setTag("user_role", data.role);
    Sentry.setTag("organization", data.organization.name);

    // item view preference
    if (data.organization.preferProductGridView) {
      dispatch(setProductView("grid"));
    }

    setAppStateInit(true);
  }, [data, dispatch, appStateInit]);

  const memoData = useMemo(() => {
    if (authState.status !== "ready" || !data) return null;
    return toAuthUserContextValue(data, currentTerritoryId, currentChannelId);
  }, [authState.status, data, currentTerritoryId, currentChannelId]);

  /**
   * Gated on session status, not just on `data`: sign-out happens in place with
   * no reload and does not evict the cached current-user query, so `data` can
   * outlive the session. Passing it ungated would let `useAuthZ()` grant to a
   * signed-out user. `authZSessionFrom` owns that gate (unit-tested).
   *
   * Not memoized — `structuralSharing` is off, so `data` identity churns every
   * refetch. Stability lives in AuthZProvider's policy fingerprint. Narrow by
   * intent; if plugins ever contribute override rules, compose them here.
   */
  const authZSession = authZSessionFrom(authState.status === "ready", data);
  const routingResources = useMemo<AuthRoutingResources>(
    () => ({
      currentUser: data,
      userError,
      activePluginsQuery: activePluginsQuery.isError
        ? { status: "error", error: activePluginsQuery.error }
        : activePluginsQuery.isPending
          ? { status: "pending" }
          : { status: "success" },
      activePluginSet,
    }),
    [
      activePluginSet,
      activePluginsQuery.error,
      activePluginsQuery.isError,
      activePluginsQuery.isPending,
      data,
      userError,
    ]
  );

  return (
    <AuthSessionContext.Provider value={authState}>
      <AuthRoutingResourcesContext.Provider value={routingResources}>
        <AuthUserProviderContext.Provider value={memoData}>
          <AuthZProvider session={authZSession}>{children}</AuthZProvider>
        </AuthUserProviderContext.Provider>
      </AuthRoutingResourcesContext.Provider>
    </AuthSessionContext.Provider>
  );
};

export default AuthUserProvider;
